import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, Modal, notification, Row } from "ant-design-vue";
import lodash from "lodash";
import moment from "moment";
import { defineAsyncComponent, reactive, toRaw } from "vue";
import { setIsLoading as setIsLoadingApp } from "../../../../app.logic";
import { callAddressType, createForm as createFormAddress } from "../../../../components/address/address.logic";
import { setAppPageHeader } from "../../../../components/layout/app-layout/app-layout.logic";
import i18n from "../../../../i18n";
import router from "../../../../router";
import store from "../../../../store";
import {
    createButton,
    createButtonActive,
    createButtonArchive,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../../utils/button";
import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickActiveAll,
    onClickArchive,
    onClickArchiveAll,
    onClickCreate,
    onClickDelete,
    onClickDeleteAll,
    onClickEdit,
    onClickView,
    watchRulesChildren
} from "../../../../utils/form";
import { createLoading } from "../../../../utils/loading";
import { has, have } from "../../../../utils/menu-context";
import {
    createAppPageHeaderTop,
    createManageActive,
    createManageArchive,
    createManageDelete,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionOpenUrl, createRowActionReStatus,
    createRowActionView
} from "../../../../utils/page";
import { flatWhereGroup } from "../../../../utils/search";
import { required, requiredWhen, validateError } from "../../../../utils/validate";
import { openModalForm as openModalFormConfirmApprove } from "../../storage-location/request/modal/confirm-approve.logic";
import { openModalForm as openModalFormConfirmReceive } from "../../storage-location/request/modal/confirm-receive.logic";
import { openModalForm as openModalFormConfirmRequest } from "../../storage-location/request/modal/confirm-request.logic";
import { callRequestDoc, callRequestStatus, callRequestType } from "../../storage-location/request/request.section";
import {
    callRequestDocGenType,
    callUser,
    callUserCompanyMe
} from "../../storage-location/request/requestor/requestor.section";
import { createForm as createFormFreeSale } from "../free-sale/free-sale.logic";
import { openModalForm as openModalFormConfirmAction } from "./modal/confirm-action.logic";
import { createConditionAction, createConditionForm } from "./request.condition";
import {
    callFreeSaleAttachment, callFreeSaleObjective, callUserObjective,
    createColumns,
    createField
} from "./request.section";

const CIcon = defineAsyncComponent(() => import("../../../../components/icon/CIcon"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/transaction/request";

export const callRequest = ({ form }) => store.dispatch(`${actionPrefix}/request`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });
export const callReStatus = ({ data }) => store.dispatch(`${actionPrefix}/reStatus`, { data });
export const callReportFreeSale = ({ data }) => store.dispatch(`${actionPrefix}/reportFreeSale`, { data });

export const onSearch = ({ props, page }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    const customWhere = lodash.cloneDeep(where);
    const companyNameThIndex = customWhere.findIndex(d => d.name === "refFreeSaleTemp.refUserCompany.companyNameTh");
    const approveCodeIndex = customWhere.findIndex(d => d.name === "refFreeSaleTemp.refHaz.approveCode");
    if (companyNameThIndex !== -1) {
        const companyNameThWhere = customWhere[companyNameThIndex];
        customWhere.splice(
            companyNameThIndex,
            1,
            {
                openBucket: true,
                name: "refFreeSaleTemp.refUserCompany.companyNameTh",
                type: "String",
                operator: "like",
                value: companyNameThWhere.value
            },
            {
                logic: "OR",
                closeBucket: true,
                name: "refFreeSaleTemp.refUserCompany.fullNameTh",
                type: "String",
                operator: "like",
                value: companyNameThWhere.value
            }
        );
    }
    if (approveCodeIndex !== -1) {
        const approveCodeWhere = customWhere[approveCodeIndex];
        customWhere.splice(
            approveCodeIndex,
            1,
            {
                openBucket: true,
                name: "refFreeSaleTemp.refHaz.approveCode",
                type: "String",
                operator: "like",
                value: approveCodeWhere.value
            },
            {
                logic: "OR",
                closeBucket: true,
                name: "refFreeSaleTemp.refInform.approveCode",
                type: "String",
                operator: "like",
                value: approveCodeWhere.value
            }
        );
    }
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { ...page, data: [], where: [...customWhere, ...createWhereFor({ props })], sort } })
        .then(page => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const validate = ({ props, data, refs }) => {
    const { menuContext } = props;
    const rulesTop = createRulesTop({ props, data, refs });
    const rules1 = createRulesTab1({ props, data, refs });
    const rules2 = createRulesTab2({ props, data, refs });
    const rules3 = createRulesTab3({ props, data, refs });
    let tabKey = null;
    return data.useForm
        .validate(Object.keys(rulesTop))
        .then(() => {
            tabKey = "Requestor";
            return data.useForm.validate(Object.keys(rules1));
        })
        .then(() => {
            tabKey = "FreeSaleInfo";
            return data.useForm.validate(Object.keys(rules2));
        })
        .then(() => {
            tabKey = "Attachment";
            return data.useForm.validate(Object.keys(rules3));
        })
        .catch(error => {
            if (tabKey) {
                menuContext.tabKey = tabKey;
            }
            validateError({ error });
            throw error;
        });
};

export const onClickSave = ({ props, data, refs, formParams, successText }) => {
    const { viewOnly, routerPrefix } = props;
    return validate({ props, data, refs }).then(() => {
        setIsLoadingApp(true);
        return callRequest({ form: { ...data.useForm.modelRef, ...formParams } })
            .finally(() => setIsLoadingApp(false))
            .then(record => {
                if (successText) notification["success"]({ message: successText });
                if (viewOnly) initForm({ props, data, refs });
                else onClickView({ record, routerPrefix });
            });
    });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createSearchOneCriteria = ({ props, id, forLog }) => {
    if (!forLog) {
        return {
            select: [
                "**",
                "refFreeSale.id",
                "refFreeSale.approveCode",
                "refFreeSale.refUserCompany.*",
                "refFreeSale.refUserCompany.refPersonType.*", 
                "refFreeSaleTemp.**",
                "refFreeSaleTemp.refUserCompany.refPersonType.*",
                "refFreeSaleTemp.refUserCompany.refCompanyType.*",
                "refFreeSaleTemp.refUserCompany.refDepartmentRegister.*",
                "refFreeSaleTemp.refUser.**",
                "refFreeSaleTemp.refHazObject.*",
                "refFreeSaleTemp.refHazObject.refSpecies.*",
                "refFreeSaleTemp.refHaz.*",
                "refFreeSaleTemp.refHaz.refType.*",
                "refFreeSaleTemp.refInformProduct.*",
                "refFreeSaleTemp.refInformProduct.refHazSpecies.*",
                "refFreeSaleTemp.refInformProduct.refHazObject.*",
                "refFreeSaleTemp.refInformProduct.refHazObject.refSpecies.*",
                "refFreeSaleTemp.refInformProduct.refHazObject.HazObjectCoordinate.refCoordinateCustoms.refUnit.*",
                "refFreeSaleTemp.refInformProduct.refHaz.*",
                "refFreeSaleTemp.refInformProduct.refHaz.refType.*",
                "refFreeSaleTemp.refInformProduct.refOriginDestinationCountry.*",
                "refFreeSaleTemp.refInform.*",
                "refFreeSaleTemp.refInform.refOperation.*",
                "refFreeSaleTemp.refInform.refTransportByWay.*",
                "refFreeSaleTemp.refInform.refTransportObjective.*",
                "refFreeSaleTemp.refInform.refOriginDestinationCountry.*",
                "refFreeSaleTemp.refInform.refImportCheckpoint.*",
                "refFreeSaleTemp.refInform.refExportCheckpoint.*",
                "refFreeSaleTemp.refInform.refPassCustoms.*",
            ],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }, ...createWhereFor({ props })],
            children: [
                {
                    name: "refFreeSaleTemp.refInform.products",
                    select: [
                        "**",
                        "refHazObject.refSpecies.*",
                        "refCoordinateCustoms.refUnit.*",
                        "refHaz.refManufactureCountry.*",
                        "refHaz.refType.*",
                        "refFreeSale.*",
                        "refHazLicense.refQuantityUnit.*"
                    ],
                    page: 0,
                    size: 1000000,
                    where: [
                        { name: "refHazSpecies.key", type: "String", operator: "!=", value: "1" },
                        { name: "refHaz.refType.key", type: "String", operator: "in", value: ["2"] }
                    ],
                    sort: [{ name: "createDate", type: "ASC" }],
                    children: [
                        {
                            name: "refHaz.products",
                            select: ["*", "refProduct.*", "refProduct.refParent.*"],
                            page: 0,
                            size: 1000000,
                            sort: [{ name: "createDate", type: "ASC" }]
                        },
                        {
                            name: "refHazObject.hazObjectCoordinates",
                            select: ["*", "refCoordinateCustoms.*", "refCoordinateCustoms.refUnit.*"],
                            page: 0,
                            size: 1000000
                        },
                        {
                            name: "refHaz.mainSubStances",
                            select: ["*", "refSubStance.*", "refUnit.*"],
                            page: 0,
                            size: 1000000,
                            sort: [{ name: "createDate", type: "ASC" }]
                        }
                    ]
                },
                {
                    name: "refFreeSaleTemp.refInform.statuses",
                    asName: "refFreeSaleTemp.refInform.lastStatus.forReceiveRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        {
                            name: "refStatus.key",
                            type: "String",
                            operator: "in",
                            value: ["waitReceive", "waitResponseCustoms", "waitApprove", "waitPayment", "approve", "notApprove", "complete"]
                        }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "refFreeSaleTemp.refHaz.statuses",
                    asName: "refFreeSaleTemp.refHaz.lastStatus.forReceiveRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        {
                            name: "refStatus.key",
                            type: "String",
                            operator: "in",
                            value: ["waitReceive", "waitResponseCustoms", "waitApprove", "waitPayment", "approve", "notApprove", "complete"]
                        }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "refFreeSaleTemp.refUserCompany.addresses",
                    select: ["*"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                },
                {
                    name: "refFreeSaleTemp.refUserCompany.userObjectives",
                    select: ["*", "refObjective.*"],
                    page: 0,
                    size: 1000000
                },
                {
                    name: "refFreeSaleTemp.refUser.addresses",
                    select: ["*"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                },
                {
                    name: "refFreeSaleTemp.addresses",
                    select: ["*", "refAddressType.*", "refProvince.*", "refAmphur.*", "refTambon.*"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refId", type: "UUID", operator: "=", value: id }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.draft",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "draft" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.waitReceive",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "waitReceive" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.waitApprove",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "waitApprove" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.approve",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "approve" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.forRequestRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "in", value: ["draft", "reEditOfficer", "reEditAuthority"] }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.forReceiveRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "waitReceive" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "feeStatements",
                    select: ["*", "refType.*", "refStatus.*"],
                    page: 0,
                    size: 1,
                    sort: [{ name: "createDate", type: "DESC" }],
                    children: []
                },
                {
                    name: "refFreeSaleTemp.products",
                    select: ["*", "refProduct.*", "refProduct.refParent.*"],
                    page: 0,
                    size: 1000000,
                    sort: [{ name: "createDate", type: "ASC" }]
                },
            ]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const createWhereFor = ({ props }) => {
    const { menuContext } = props;
    const whereGroup = [];
    const userInfo = store.state.resource.auth.info?.user;
    whereGroup.push([{ name: "createBy", type: "String", operator: "=", value: userInfo.username }]);
    if (has({ menuContext, key: "forRequest" })) {
        whereGroup.push([{ name: "refFreeSaleTemp.refUser.username", type: "String", operator: "=", value: userInfo.username }]);
        if (userInfo.isCeo) {
            whereGroup.push([{ name: "refFreeSaleTemp.refUserCompany.id", type: "UUID", operator: "=", value: userInfo?.refParent?.id || userInfo?.id }]);
        }
    }
    if (has({ menuContext, key: "forReceive" })) {
        whereGroup.push([
            { name: "refStatus.key", type: "String", operator: "!=", value: "draft" },
            { name: "refDepartment.id", type: "UUID", operator: "=", value: userInfo.refDepartment?.id }
        ]);
    }
    if (has({ menuContext, key: "forApprove" })) {
        whereGroup.push([{ name: "refToApproveBy.username", type: "String", operator: "=", value: userInfo.username }]);
    }
    if (has({ menuContext, key: "canViewAll" })) {
        whereGroup.push([{ name: "refStatus.key", type: "String", operator: "!=", value: "draft" },]);
    }
    return [
        { name: "refRequestDoc.key", type: "String", operator: "in", value: ["22"] },
        ...flatWhereGroup({ where: whereGroup, logic: "OR" })
    ];
};

export const createForm = () => {
    return {
        model: null,
        refModelId: null,
        refModelTempId: null,
        refRequestType: null,
        refRequestDoc: null,
        refStatus: null,
        draftCode: null,
        requestCode: null,
        refDepartment: null,
        isSignCa: null,
        refFreeSale: null,
        refFreeSaleTemp: { ...createFormFreeSale(), addresses: [createFormAddress()] },
        lastStatus: {
            draft: [{ remark: null, statusDatetime: null }],
            waitReceive: [{ remark: null, statusDatetime: null }],
            waitApprove: [{ remark: null, statusDatetime: null }],
            approve: [{ remark: null, statusDatetime: null }],
            forRequestRemark: [{ remark: null, statusDatetime: null }],
            forReceiveRemark: [{ remark: null, statusDatetime: null }]
        }
    };
};

export const createRules = ({ props, data, refs, key }) => {
    return {
        ...createRulesTop({ props, data, refs, key }),
        ...createRulesTab1({ props, data, refs, key }),
        ...createRulesTab2({ props, data, refs, key }),
        ...createRulesTab3({ props, data, refs, key })
    };
};

export const createRulesTop = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return {
        [`${prefix}refDocGenType`]: [required],
        [`${prefix}requestCode`]: [requiredWhen({ when: () => data.sendReceive && ["2"].includes(model.refDocGenType?.key) })],
        [`${prefix}refFreeSaleTemp.approveCode`]: [requiredWhen({ when: () => data.sendReceive && ["2"].includes(model.refDocGenType?.key) })],
        [`${prefix}refRequestType`]: [required],
        [`${prefix}refFreeSaleTemp.refObjective`]: [required],
        [`${prefix}lastStatus.forRequestRemark.0.remark`]: [requiredWhen({ when: () => !model.isOfficerCreate && ["2", "4"].includes(model.refRequestType?.key) && (!model.id || ["draft", "reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(model.refStatus?.key)) })]
    };
};

export const createRulesTab1 = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return {
        [`${prefix}refDepartment`]: [required],
        [`${prefix}refFreeSaleTemp.refUserCompany`]: [
            requiredWhen({ when: () => true, requiredValidator: () => model.refFreeSaleTemp.refUserCompany?.id })
        ],
        [`${prefix}refFreeSaleTemp.refUser`]: [requiredWhen({ when: () => true, requiredValidator: () => model.refFreeSaleTemp.refUser?.id })]
    };
};

export const createRulesTab2 = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {
        [`${prefix}refFreeSaleTemp.refHaz`]: [requiredWhen({ when: () => ["1"].includes(model.refFreeSaleTemp?.refObjective?.key) })],
        [`${prefix}refFreeSaleTemp.refInform`]: [requiredWhen({ when: () => ["2"].includes(model.refFreeSaleTemp?.refObjective?.key) })],
        [`${prefix}refFreeSaleTemp.manufacturerEn`]: [required],
        [`${prefix}refFreeSaleTemp.addresses.0.noEn`]: [required],
        [`${prefix}refFreeSaleTemp.addresses.0.refProvince`]: [required],
        [`${prefix}refFreeSaleTemp.addresses.0.refAmphur`]: [required],
        [`${prefix}refFreeSaleTemp.addresses.0.refTambon`]: [required],
        [`${prefix}refFreeSaleTemp.addresses.0.postCode`]: [required],
        [`${prefix}refFreeSaleTemp.refCountry`]: [required],

        [`${prefix}refFreeSaleTemp.refInformProduct`]: [requiredWhen({ when: () => ["2"].includes(model.refFreeSaleTemp?.refObjective?.key) })],
    };
    return rules;
};

export const createRulesTab3 = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {};
    model?.refFreeSaleTemp?.attachments?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}refFreeSaleTemp.attachments.${index}.documentName`] = [required];
            rules[`${prefix}refFreeSaleTemp.attachments.${index}.size`] = [
                requiredWhen({
                    when: () =>
                        model.refFreeSaleTemp.attachments[index].attribute?.isRequiredAttachment ||
                        model.refFreeSaleTemp.attachments[index].attribute?.documentNo
                })
            ];
        }
    });
    return rules;
};

export const createFormTitle = ({ data }) => {
    const modelRef = data.useForm.modelRef;
    return modelRef.id ? `${modelRef.refRequestDoc?.value} (${modelRef.refStatus?.value})` : i18n.global.t("button.create");
};

export const loadForm = ({ props, data, refs }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ props, data, refs, id }) });
    } else {
        promise = Promise.resolve({ data: [useForm.modelRef] });
    }
    return promise
        .then(page => initUseForm({ props, data, refs, page }))
        .then(() => loadDefaultForm({ props, data, refs }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] } }))
        .finally(() => setIsLoading(false));
};

export const loadDefaultForm = ({ props, data }) => {
    const { form, forceDefault } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    const promises = [
        callFreeSaleAttachment({}),
        callRequestStatus({}),
        callRequestType({}),
        callRequestDoc({}),
        callUserObjective({}),
        callRequestDocGenType({}),
        callAddressType({}),
        callFreeSaleObjective({})
    ];
    return Promise.all(promises).then(arr => {
        data.freeSaleAttachments = arr[0];
        data.statuses = arr[1];
        data.requestTypes = arr[2].filter(d => !["3"].includes(d.key));
        data.requestDocs = arr[3];
        data.userObjectives = arr[4];
        data.requestDocGenTypes = arr[5];
        data.addressTypes = arr[6];
        data.freeSaleObjectives = arr[7];

        const userInfo = store.state.resource.auth.info?.user;
        if ((!id && !form) || forceDefault) {
            modelRef.isOfficerCreate = !!userInfo.refOfficerLv;
            if (!modelRef.isOfficerCreate) {
                callUserCompanyMe({}).then(userCompanies => {
                    const refUserCompany = userCompanies[0];
                    modelRef.refFreeSaleTemp.refUserCompany = refUserCompany;
                    if (refUserCompany) {
                        callUser({ userCompanyId: refUserCompany.id }).then(
                            users => (modelRef.refFreeSaleTemp.refUser = users.find(d => d.id === userInfo?.id))
                        );
                    }
                });
            }
            modelRef.lastStatus.draft[0].statusDatetime = moment().format("YYYY-MM-DD[T]HH:mm:ssZ");

            modelRef.refRequestType = data.requestTypes.find(d => d.key === "1");
            modelRef.refRequestDoc = data.requestDocs.find(d => d.key === "22");
            modelRef.refFreeSaleTemp.refRequestDoc = data.requestDocs.find(d => d.key === "22");
            modelRef.refDocGenType = data.requestDocGenTypes.find(d => d.key === "1");

            modelRef.refFreeSaleTemp.addresses[0].refAddressType = data.addressTypes?.find(d => d.key === "1");
            modelRef.refFreeSaleTemp.refObjective = data.freeSaleObjectives?.find(d => d.key === "1");
            modelRef.refFreeSaleTemp.refOperation = data.userObjectives.find(d => d.key === "4");
            data.useFreeSaleAttachments =  modelRef.refFreeSaleTemp.refObjective.key === '1' ?
                data.freeSaleAttachments.filter(d => ["1"].includes(d.key))
                :  data.freeSaleAttachments.filter(d => ["2", "3"].includes(d.key))
            data.useFreeSaleAttachments.forEach(item =>
                modelRef.refFreeSaleTemp.attachments.push({ documentName: item.value, attribute: lodash.cloneDeep(item.attribute) })
            );
        } else if (id || form) {
            if(!!modelRef.refFreeSaleTemp?.refObjective && (!!modelRef.refFreeSaleTemp?.refHaz || !!modelRef.refFreeSaleTemp?.refInform)) {
                if (modelRef.refFreeSaleTemp?.refObjective?.key === "1") {
                    modelRef.refFreeSaleTemp.freeSaleProducts.push({
                        refHaz: {
                            approveCode: modelRef.refFreeSaleTemp?.refHaz.approveCode,
                            tradeNameEn: modelRef.refFreeSaleTemp?.tradeNameEn,
                            products: modelRef.refFreeSaleTemp?.products,
                            toUse: modelRef.refFreeSaleTemp?.toUse,
                            refType: modelRef.refFreeSaleTemp?.refType
                        }
                    });
                }
                else if (modelRef.refFreeSaleTemp?.refObjective?.key === "2") {
                    modelRef.refFreeSaleTemp.freeSaleProducts = modelRef.refFreeSaleTemp?.refInform?.products
                        .filter(item => item.refHazSpecies?.key !== "1" )
                        .filter(item =>  !item.refFreeSale || item.id === modelRef.refFreeSaleTemp?.refInformProduct?.id)
                }
            }
        }
    });
};

export const initState = ({ props }) => {
    const { menuContext, routerPrefix } = props;
    state.manages = [
        createManageSelect({ menuContext }),
        createManageActive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickActiveAll({ rowSelection, callActiveAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") })
        }),
        createManageArchive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickArchiveAll({ rowSelection, callArchiveAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") }),
            getCheckboxProps: ({ record }) => ({ disabled: !state.conditionAction.canArchive({ props, record }) })
        }),
        createManageDelete({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickDeleteAll({ rowSelection, callDeleteAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") }),
            getCheckboxProps: ({ record }) => ({ disabled: !state.conditionAction.canDelete({ props, record }) })
        }),
        createManageExportCsv({ menuContext })
    ];
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) => onClickEdit({ record, routerPrefix }),
            visible: ({ record }) => state.conditionAction.canEdit({ props, record })
        }),
        createRowActionReStatus({
            menuContext,
            onClick: ({ record }) => {
                Modal.confirm({
                    title: i18n.global.t("components.table.operatorAndPagination.youAreWant", { text: i18n.global.t("button.reStatus") }),
                    autoFocusButton: "cancel",
                    onOk: () => {
                        setIsLoadingApp(true);
                        callReStatus({ data: record })
                            .finally(() => setIsLoadingApp(false))
                            .then(() => onSearch({ props, page: state.page }));
                    }
                });
            },
            visible: ({ record }) => state.conditionAction.canReStatus({ props, record })
        }),
        /*
        createRowActionPdf({
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="search.svg"></CIcon> },
            text: () => i18n.global.t("button.viewRequestDocument"),
            callReport: ({ record }) => callReportFreeSale({ data: { id: record.id } }),
            fileName: "report.pdf"
        })
        */
        createRowActionOpenUrl({
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="search.svg"></CIcon> },
            text: () => i18n.global.t("button.viewRequestDocument"),
            callUrl: ({ record }) => `${process.env.VUE_APP_API_REPORT_URL}/files/haz/company/${record.id}/file-result.pdf?contentDisposition=inline&_timestamp=${new Date().getTime()}`,
            visible: ({ record }) => state.conditionAction.canReport({ props, record })
        })
    ];
};

export const initForm = ({ props, data, refs }) => {
    const { routerPrefix } = props;
    checkAttribute({ props, data, refs });
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    data.userInfo = store.state.resource.auth.info?.user;
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => data.useForm.modelRef.refFreeSaleTemp.attachments.filter(d => d.isDelete !== true).length,
        createRules: () => createRulesTab3({ props, data, refs }),
        itemsKey: "refFreeSaleTemp.attachments"
    });
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs })
                }
            });
        }
    });
};

export const createAppPageHeaderDefault = ({ props, data, refs }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly, routerPrefix } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive, feeStatements } = modelRef;
    const canEdit = state.conditionAction.canEdit({ props, record: initialModel });
    const canWaitReceive = state.conditionAction.canWaitReceive({ props, record: initialModel });
    const canWaitApprove = state.conditionAction.canWaitApprove({ props, record: initialModel });
    const canApprove = state.conditionAction.canApprove({ props, record: initialModel });
    const canDelete = state.conditionAction.canDelete({ props, record: initialModel });

    const lifetime = calLifetimeMonthsRequestDoc({ requestDoc: data.requestDocs.find(d => d.key === "04") });

    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && canEdit) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        col1.push(
            createButtonSave({
                onClick: () => {
                    data.sendReceive = false;
                    const refFreeSaleTemp = modelRef.refFreeSaleTemp;
                    let statusRemark = null;
                    if ([undefined, "draft", "reEditOfficer", "reEditAuthority"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forRequestRemark[0].remark;
                    } else if (["waitReceive"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forReceiveRemark[0].remark;
                    }
                    validate({ props, data, refs }).then(() => {
                        const hazExpireDate = moment(modelRef.refFreeSaleTemp?.refHaz?.expireDate, 'YYYY-MM-DD');
                        const duration = moment.duration(moment().diff(hazExpireDate));
                        const months = duration.asMonths();

                        if(months >= lifetime-2) {
                            openModalFormConfirmAction({
                                onOk: () => {
                                    if (months >= lifetime) {
                                        notification["error"]({ message: i18n.global.t("views.request.freeSale.modal.resultFailed") + "\r\n" + i18n.global.t("views.request.freeSale.modal.causeFailed1") })
                                    } else {
                                        onClickSave({
                                            props,
                                            data,
                                            refs,
                                            formParams: {
                                                refFreeSaleTemp: {
                                                    ...modelRef.refFreeSaleTemp,
                                                    refUserCompany: refFreeSaleTemp?.refUserCompany?.id ? refFreeSaleTemp?.refUserCompany : null,
                                                    refUser: refFreeSaleTemp?.refUser?.id ? refFreeSaleTemp?.refUser : null
                                                },
                                                statusRemark
                                            }
                                        });
                                    }
                                }
                            });
                        } else {
                            onClickSave({
                                props,
                                data,
                                refs,
                                formParams: {
                                    refFreeSaleTemp: {
                                        ...modelRef.refFreeSaleTemp,
                                        refUserCompany: refFreeSaleTemp?.refUserCompany?.id ? refFreeSaleTemp?.refUserCompany : null,
                                        refUser: refFreeSaleTemp?.refUser?.id ? refFreeSaleTemp?.refUser : null
                                    },
                                    statusRemark
                                }
                            });
                        }
                    });
                },
                classStyle: "m-1"
            })
        );
    }
    if (id && isActive && canWaitReceive) {
        const statusWaitReceive = data.statuses.find(status => status.key === "waitReceive");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${statusWaitReceive?.attribute?.actionText}`,
                classStyle: `m-1 ${statusWaitReceive?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    validate({ props, data, refs }).then(() => {
                        // todo: recheck use haz expireDate form
                        //      --> refFreeSaleTemp.refHaz.expireDate
                        //      --> OR refFreeSaleTemp.hazExpireDate
                        const hazExpireDate = moment(modelRef.refFreeSaleTemp?.refHaz?.expireDate, 'YYYY-MM-DD');
                        const duration = moment.duration(moment().diff(hazExpireDate));
                        const months = duration.asMonths();
                        if(months >= lifetime-2) {
                            openModalFormConfirmAction({
                                onOk: () => {
                                    if (months >= lifetime) {
                                        notification["error"]({ message: i18n.global.t("views.request.freeSale.modal.resultFailed") + "\r\n" + i18n.global.t("views.request.freeSale.modal.causeFailed1") })
                                    } else {
                                        openModalFormConfirmRequest({
                                            onOk: record => {
                                                const refFreeSaleTemp = modelRef.refFreeSaleTemp
                                                onClickSave({
                                                    props,
                                                    data,
                                                    refs,
                                                    formParams: {
                                                        refFreeSaleTemp: {
                                                            ...modelRef.refFreeSaleTemp,
                                                            refUserCompany: refFreeSaleTemp?.refUserCompany?.id ? refFreeSaleTemp?.refUserCompany : null,
                                                            refUser: refFreeSaleTemp?.refUser?.id ? refFreeSaleTemp?.refUser : null
                                                        },
                                                        statusRemark: modelRef.lastStatus.forReceiveRemark[0].remark,
                                                        statusRemarkBefore: modelRef.lastStatus.forRequestRemark[0].remark,
                                                        refStatus: statusWaitReceive,
                                                        isSignCa: record.isSignCa,
                                                        passwordCa: record.passwordCa
                                                    },
                                                    successText: i18n.global.t("views.request.freeSale.modal.resultSuccess")
                                                });
                                            }
                                        });
                                    }
                                }
                            });
                        } else {
                            openModalFormConfirmRequest({
                                onOk: record => {
                                    const refFreeSaleTemp = modelRef.refFreeSaleTemp
                                    onClickSave({
                                        props,
                                        data,
                                        refs,
                                        formParams: {
                                            refFreeSaleTemp: {
                                                ...modelRef.refFreeSaleTemp,
                                                refUserCompany: refFreeSaleTemp?.refUserCompany?.id ? refFreeSaleTemp?.refUserCompany : null,
                                                refUser: refFreeSaleTemp?.refUser?.id ? refFreeSaleTemp?.refUser : null
                                            },
                                            statusRemark: modelRef.lastStatus.forReceiveRemark[0].remark,
                                            statusRemarkBefore: modelRef.lastStatus.forRequestRemark[0].remark,
                                            refStatus: statusWaitReceive,
                                            isSignCa: record.isSignCa,
                                            passwordCa: record.passwordCa
                                        },
                                        successText: i18n.global.t("views.request.modal.request.sendSuccess")
                                    });
                                }
                            });
                        }
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (id && isActive && canWaitApprove) {
        const statusWaitApprove = data.statuses.find(status => status.key === "waitApprove");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${i18n.global.t("button.sendRequest")}`,
                classStyle: `m-1 ${statusWaitApprove?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    validate({ props, data, refs }).then(() => {
                        openModalFormConfirmReceive({
                            props: {
                                roleName: "FREE_SALE_APPROVE",
                                statusKeys: modelRef.isOfficerCreate ? ["waitApprove"] : []
                            },
                            onOk: record => {
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    formParams: {
                                        statusRemark: modelRef.lastStatus.forRequestRemark[0].remark,
                                        statusRemarkBefore: modelRef.lastStatus.forReceiveRemark[0].remark,
                                        refStatus: record.refStatus,
                                        refToApproveBy: record.refToApproveBy
                                    },
                                    successText: i18n.global.t(`views.request.modal.receive.${record.refStatus?.key}`)
                                });
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (id && isActive && canApprove) {
        const statusApprove = data.statuses.find(status => status.key === "approve");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${i18n.global.t("button.sendRequest")}`,
                classStyle: `m-1 ${statusApprove?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    validate({ props, data, refs }).then(() => {
                        openModalFormConfirmApprove({
                            onOk: record => {
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    formParams: { refStatus: record.refStatus },
                                    successText: i18n.global.t(`views.request.modal.approve.${record.refStatus?.key}`)
                                });
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && canDelete) {
        col2.push(
            createButtonDelete({
                onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }),
                classStyle: "m-1"
            })
        );
    }
    if (
        id &&
        feeStatements?.[0]?.id &&
        ((has({ menuContext, key: "forRequest" }) && ![undefined, "draft"].includes(feeStatements?.[0]?.refStatus?.key)) ||
            have({ menuContext, keys: ["forReceive", "forApprove"] }))
    ) {
        col2.push(
            createButton({
                text: `${i18n.global.t("model.feeStatement.modelName")}${feeStatements?.[0] ? ` (${feeStatements[0].refStatus?.value})` : ""}`,
                classStyle: `m-1 btn-primary`,
                onClick: () => {
                    validate({ props, data, refs }).then(() => {
                        router.push({ name: `fees-view`, params: { id: feeStatements?.[0]?.id } });
                    });
                },
                icon: <CIcon file="active.svg"></CIcon>
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const state = reactive({
    columns: createColumns(),
    page: createPage({
        options: {
            select: [
                "*",
                "refFreeSale.*",
                "refFreeSaleTemp.*",
                "refFreeSaleTemp.refUserCompany.*",
                "refFreeSaleTemp.refUserCompany.refPersonType.*",
                "refFreeSaleTemp.refUser.*",
                "refFreeSaleTemp.refObjective.*",
                "refFreeSaleTemp.refHaz.*",
                "refFreeSaleTemp.refHaz.refHazObject.*",
                "refFreeSaleTemp.refInform.*",
                "refFreeSaleTemp.refInformProduct.*",
                "refFreeSaleTemp.refInformProduct.refHaz.*",
                // "refFreeSaleTemp.refInformProduct.refOriginDestinationCountry.*",
                "refRequestType.*",
                "refStatus.*"
            ],
            sort: [/* { name: "refStatus.seq", type: "ASC" } ,*/ { name: "updateDate", type: "DESC" }]
        }
    }),
    tableOptions: { search: { activeKey: "Search" } },
    conditionAction: createConditionAction()
});

export const calLifetimeMonthsRequestDoc = ({ requestDoc }) => {
    let lifetime = requestDoc?.attribute?.expire

    if (requestDoc?.attribute?.expireUnit === "YEARS") {
        lifetime *= 12
    } else if (requestDoc?.attribute?.expireUnit === "MONTHS") {
        lifetime *= 1
    }

    return lifetime;

}